import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Component, UoM,
  SearchComponentsRequest, SearchComponentsResponse,
  CreateComponentRequest
} from '../../services/componentApi';

export interface ComponentState {
  // Component State
  components: Component[];
  selectedComponent: Component | null;
  componentLoading: boolean;
  componentError: string | null;
  componentValidationErrors: string[];
  
  // Pagination and Filtering
  componentPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  
  // Search and Filters
  componentSearchTerm: string;
  componentActiveFilter: boolean | null;
  
  // Dropdown Data
  dropdownUoMs: UoM[];
  dropdownsLoading: boolean;
  
  // UI State
  componentDialogOpen: boolean;
  
  // Export State
  exportLoading: boolean;
  exportError: string | null;
}

const initialState: ComponentState = {
  // Component State
  components: [],
  selectedComponent: null,
  componentLoading: false,
  componentError: null,
  componentValidationErrors: [],
  
  // Pagination and Filtering
  componentPagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  },
  
  // Search and Filters
  componentSearchTerm: '',
  componentActiveFilter: null,
  
  // Dropdown Data
  dropdownUoMs: [],
  dropdownsLoading: false,
  
  // UI State
  componentDialogOpen: false,
  
  // Export State
  exportLoading: false,
  exportError: null,
};

const componentSlice = createSlice({
  name: 'component',
  initialState,
  reducers: {
    // Component Actions
    fetchComponentsStart: (state, action: PayloadAction<SearchComponentsRequest | undefined>) => {
      state.componentLoading = true;
      state.componentError = null;
    },

    fetchComponentsSuccess: (state, action: PayloadAction<SearchComponentsResponse>) => {
      state.componentLoading = false;
      state.components = action.payload.data;
      state.componentPagination = {
        currentPage: action.payload.page,
        pageSize: action.payload.limit,
        totalCount: action.payload.totalCount,
        totalPages: action.payload.totalPages,
      };
    },

    fetchComponentsFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },

    fetchComponentByIdStart: (state, action: PayloadAction<string>) => {
      state.componentLoading = true;
      state.componentError = null;
    },

    fetchComponentByIdSuccess: (state, action: PayloadAction<Component>) => {
      state.componentLoading = false;
      state.selectedComponent = action.payload;
    },

    fetchComponentByIdFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },

    createComponentStart: (state, action: PayloadAction<CreateComponentRequest>) => {
      state.componentLoading = true;
      state.componentError = null;
      state.componentValidationErrors = [];
    },

    createComponentSuccess: (state, action: PayloadAction<Component>) => {
      state.componentLoading = false;
      state.components.unshift(action.payload);
      state.selectedComponent = action.payload;
      state.componentDialogOpen = false;
    },

    createComponentFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },

    updateComponentStart: (state, action: PayloadAction<Partial<Component>>) => {
      state.componentLoading = true;
      state.componentError = null;
      state.componentValidationErrors = [];
    },

    updateComponentSuccess: (state, action: PayloadAction<Component>) => {
      state.componentLoading = false;
      const index = state.components.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.components[index] = action.payload;
      }
      state.selectedComponent = action.payload;
    },

    updateComponentFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },

    deleteComponentStart: (state, action: PayloadAction<string>) => {
      state.componentLoading = true;
      state.componentError = null;
    },

    deleteComponentSuccess: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.components = state.components.filter(c => c.id !== action.payload);
    },

    deleteComponentFailure: (state, action: PayloadAction<string>) => {
      state.componentLoading = false;
      state.componentError = action.payload;
    },

    // Dropdown Actions
    fetchDropdownsStart: (state) => {
      state.dropdownsLoading = true;
    },

    fetchDropdownsSuccess: (state, action: PayloadAction<{
      uoms?: UoM[];
    }>) => {
      state.dropdownsLoading = false;
      if (action.payload.uoms) state.dropdownUoMs = action.payload.uoms;
    },

    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.dropdownsLoading = false;
      state.componentError = action.payload;
    },

    // Search and Filter Actions
    setComponentSearchTerm: (state, action: PayloadAction<string>) => {
      state.componentSearchTerm = action.payload;
      state.componentPagination.currentPage = 1;
    },

    setComponentActiveFilter: (state, action: PayloadAction<boolean | null>) => {
      state.componentActiveFilter = action.payload;
      state.componentPagination.currentPage = 1;
    },

    setComponentPagination: (state, action: PayloadAction<Partial<ComponentState['componentPagination']>>) => {
      state.componentPagination = { ...state.componentPagination, ...action.payload };
    },

    // UI Actions
    setSelectedComponent: (state, action: PayloadAction<Component | null>) => {
      state.selectedComponent = action.payload;
    },

    setComponentDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.componentDialogOpen = action.payload;
    },

    // Validation Actions
    setComponentValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.componentValidationErrors = action.payload;
    },

    clearComponentError: (state) => {
      state.componentError = null;
      state.componentValidationErrors = [];
    },

    // Export Actions
    exportComponentStart: (state) => {
      state.exportLoading = true;
      state.exportError = null;
    },

    exportComponentSuccess: (state) => {
      state.exportLoading = false;
    },

    exportComponentFailure: (state, action: PayloadAction<string>) => {
      state.exportLoading = false;
      state.exportError = action.payload;
    },
  },
});

export const {
  // Component Actions
  fetchComponentsStart, fetchComponentsSuccess, fetchComponentsFailure,
  fetchComponentByIdStart, fetchComponentByIdSuccess, fetchComponentByIdFailure,
  createComponentStart, createComponentSuccess, createComponentFailure,
  updateComponentStart, updateComponentSuccess, updateComponentFailure,
  deleteComponentStart, deleteComponentSuccess, deleteComponentFailure,
  
  // Dropdown Actions
  fetchDropdownsStart, fetchDropdownsSuccess, fetchDropdownsFailure,
  
  // Search and Filter Actions
  setComponentSearchTerm, setComponentActiveFilter, setComponentPagination,
  
  // UI Actions
  setSelectedComponent, setComponentDialogOpen,
  
  // Validation Actions
  setComponentValidationErrors, clearComponentError,
  
  // Export Actions
  exportComponentStart, exportComponentSuccess, exportComponentFailure,
} = componentSlice.actions;

export default componentSlice.reducer; 